const LANE_WIDTH = 2.3;
const ROW_LENGTH = 6;
let materialCache = null;
const themedMaterialCache = new Map();

function getMaterials(Three) {
  if (materialCache?.Three === Three) return materialCache;
  const standard = (color, options = {}) => {
    const parameters = {
      color,
      roughness: options.roughness ?? 0.85,
      metalness: options.metalness ?? 0.08,
      emissive: options.emissive ?? 0,
      emissiveIntensity: options.emissiveIntensity ?? 0,
      transparent: options.transparent ?? false,
      opacity: options.opacity ?? 1,
    };
    if (options.side !== undefined) parameters.side = options.side;
    const material = new Three.MeshStandardMaterial(parameters);
    if (typeof material.onBuild !== "function") material.onBuild = () => {};
    return material;
  };
  const portalCore = new Three.MeshBasicMaterial({
    color: 0x7c5cff,
    transparent: true,
    opacity: 0.2,
    side: Three.DoubleSide,
    depthWrite: false,
  });
  if (typeof portalCore.onBuild !== "function") portalCore.onBuild = () => {};
  materialCache = {
    Three,
    bark: standard(0x473522, { roughness: 0.96 }),
    barkLight: standard(0x725237, { roughness: 0.92 }),
    moss: standard(0x237a38, {
      roughness: 0.88,
      emissive: 0x0b2b18,
      emissiveIntensity: 0.26,
    }),
    leaf: standard(0x2ebc58, {
      roughness: 0.82,
      emissive: 0x0a401d,
      emissiveIntensity: 0.32,
    }),
    leafDark: standard(0x125f2c, { roughness: 0.9 }),
    stone: standard(0x394944, { roughness: 0.87 }),
    stoneLight: standard(0x687a6f, { roughness: 0.82 }),
    cyan: standard(0x42fbde, {
      roughness: 0.22,
      metalness: 0.55,
      emissive: 0x19d9bf,
      emissiveIntensity: 1.15,
    }),
    gold: standard(0xf5cf4a, {
      roughness: 0.32,
      metalness: 0.62,
      emissive: 0x9c6a0b,
      emissiveIntensity: 0.65,
    }),
    water: standard(0x46ccff, {
      roughness: 0.15,
      emissive: 0x0878a8,
      emissiveIntensity: 0.85,
      transparent: true,
      opacity: 0.54,
      side: Three.DoubleSide,
    }),
    portal: standard(0x30f7e6, {
      roughness: 0.16,
      metalness: 0.7,
      emissive: 0x14e7d5,
      emissiveIntensity: 2,
    }),
    portalCore,
  };
  return materialCache;
}

function mesh(Three, geometry, material, position, rotation, scale) {
  const object = new Three.Mesh(geometry, material);
  if (position) object.position.set(...position);
  if (rotation) object.rotation.set(...rotation);
  if (scale) object.scale.set(...scale);
  return object;
}

function themedMaterial(Three, key, color, emissive, options = {}) {
  const cacheKey = `${key}:${color}:${emissive}:${options.opacity ?? 1}`;
  if (themedMaterialCache.has(cacheKey)) return themedMaterialCache.get(cacheKey);
  const parameters = {
    color,
    emissive,
    emissiveIntensity: options.emissiveIntensity ?? 0.75,
    roughness: options.roughness ?? 0.48,
    metalness: options.metalness ?? 0.24,
    transparent: options.opacity !== undefined && options.opacity < 1,
    opacity: options.opacity ?? 1,
    depthWrite: options.depthWrite ?? true,
  };
  if (options.side !== undefined) parameters.side = options.side;
  const material = new Three.MeshStandardMaterial(parameters);
  if (typeof material.onBuild !== "function") material.onBuild = () => {};
  themedMaterialCache.set(cacheKey, material);
  return material;
}

function createTree(Three, { giant = false } = {}) {
  const mat = getMaterials(Three);
  const group = new Three.Group();
  const height = giant ? 12 : 4.8;
  const trunkRadius = giant ? 2.25 : 0.68;
  group.name = giant ? "MayhemGiantTree" : "JungleTreeObstacle";
  group.add(
    mesh(
      Three,
      new Three.CylinderGeometry(trunkRadius * 0.64, trunkRadius, height, giant ? 14 : 9),
      mat.bark,
      [0, height / 2, 0],
      [0.04, 0, -0.03],
    ),
  );

  const branchCount = giant ? 9 : 5;
  for (let index = 0; index < branchCount; index += 1) {
    const angle = (index / branchCount) * Math.PI * 2;
    const length = giant ? 5.8 : 2.15;
    group.add(
      mesh(
        Three,
        new Three.CylinderGeometry(
          giant ? 0.22 : 0.08,
          giant ? 0.48 : 0.19,
          length,
          8,
        ),
        index % 2 ? mat.barkLight : mat.bark,
        [
          Math.cos(angle) * (giant ? 1.35 : 0.42),
          height * 0.72 + (index % 2) * 0.55,
          Math.sin(angle) * (giant ? 1.35 : 0.42),
        ],
        [Math.sin(angle) * 0.85, 0, Math.cos(angle) * 0.85],
      ),
    );
  }

  const canopyCount = giant ? 20 : 7;
  for (let index = 0; index < canopyCount; index += 1) {
    const angle = (index / canopyCount) * Math.PI * 2;
    const radius = giant ? 5 + (index % 3) * 0.55 : 1.55 + (index % 2) * 0.28;
    group.add(
      mesh(
        Three,
        new Three.DodecahedronGeometry(giant ? 1.75 : 0.82, 0),
        index % 3 === 0 ? mat.leafDark : mat.leaf,
        [
          Math.cos(angle) * radius,
          height + (index % 4) * (giant ? 0.66 : 0.2),
          Math.sin(angle) * radius,
        ],
        [index * 0.2, index * 0.34, 0],
        [1.34, 0.8, 1.16],
      ),
    );
  }

  const vineCount = giant ? 8 : 3;
  for (let index = 0; index < vineCount; index += 1) {
    const angle = (index / vineCount) * Math.PI * 2;
    group.add(
      mesh(
        Three,
        new Three.CylinderGeometry(giant ? 0.065 : 0.035, giant ? 0.085 : 0.045, height * 0.52, 6),
        mat.moss,
        [
          Math.cos(angle) * (giant ? 3.4 : 1.1),
          height * 0.57,
          Math.sin(angle) * (giant ? 3.4 : 1.1),
        ],
        [0.06 * Math.sin(angle), 0, 0.06 * Math.cos(angle)],
      ),
    );
  }
  return group;
}

function createRuinArch(Three, clearance = 7) {
  const mat = getMaterials(Three);
  const group = new Three.Group();
  const columnHeight = Math.max(5.5, clearance);
  for (const side of [-1, 1]) {
    for (let tier = 0; tier < 4; tier += 1) {
      group.add(
        mesh(
          Three,
          new Three.BoxGeometry(1.1, columnHeight / 4 - 0.08, 1.35),
          tier % 2 ? mat.stoneLight : mat.stone,
          [side * 6.5 + (tier % 2) * 0.12 * side, 0.7 + tier * (columnHeight / 4), 0],
          [0, tier % 2 ? 0.08 * side : -0.04 * side, tier % 3 === 0 ? 0.03 * side : 0],
        ),
      );
    }
  }
  for (const [x, width, tilt] of [
    [-4.7, 3.4, -0.08],
    [-1.55, 3.25, 0.04],
    [1.65, 3.1, -0.06],
    [4.7, 2.75, 0.1],
  ]) {
    group.add(
      mesh(
        Three,
        new Three.BoxGeometry(width, 0.82, 1.3),
        mat.stone,
        [x, columnHeight + 0.42 + Math.abs(x) * 0.04, 0],
        [0, tilt, tilt],
      ),
    );
  }
  group.add(
    mesh(
      Three,
      new Three.BoxGeometry(4.8, 0.16, 1.4),
      mat.moss,
      [-3.2, columnHeight + 0.9, 0],
      [0, 0.04, -0.08],
    ),
  );
  return group;
}

function createWaterfall(Three, side = 1) {
  const mat = getMaterials(Three);
  const group = new Three.Group();
  group.add(
    mesh(
      Three,
      new Three.PlaneGeometry(7, 13, 8, 14),
      mat.water,
      [side * 11.5, 5.5, -1],
    ),
  );
  for (let index = 0; index < 5; index += 1) {
    group.add(
      mesh(
        Three,
        new Three.BoxGeometry(0.1 + index * 0.025, 11.2, 0.08),
        mat.cyan,
        [side * (9.6 + index * 0.7), 5.7, -0.7],
      ),
    );
  }
  group.add(
    mesh(
      Three,
      new Three.TorusGeometry(3.8, 0.22, 8, 32),
      mat.water,
      [side * 11.5, -0.45, -1],
      [Math.PI / 2, 0, 0],
      [1, 1, 0.5],
    ),
  );
  return group;
}

function createTempleCourt(Three) {
  const mat = getMaterials(Three);
  const group = new Three.Group();
  for (const side of [-1, 1]) {
    for (let index = 0; index < 3; index += 1) {
      const x = side * (8.8 + index * 2.1);
      group.add(
        mesh(
          Three,
          new Three.CylinderGeometry(0.58, 0.72, 4.8 - index * 0.35, 8),
          mat.stone,
          [x, 2.05 - index * 0.18, index * 0.8],
          [0.02 * index, 0, 0.03 * side],
        ),
      );
      group.add(
        mesh(
          Three,
          new Three.BoxGeometry(1.7, 0.32, 1.7),
          mat.moss,
          [x, 4.25 - index * 0.35, index * 0.8],
        ),
      );
    }
  }
  return group;
}

function createRootBridge(Three, side = -1) {
  const mat = getMaterials(Three);
  const group = new Three.Group();
  for (let index = 0; index < 7; index += 1) {
    group.add(
      mesh(
        Three,
        new Three.CylinderGeometry(0.17, 0.29, 8.5, 7),
        index % 2 ? mat.barkLight : mat.bark,
        [side * (8.4 + index * 0.36), 0.15 + index * 0.14, -2 + index * 0.65],
        [Math.PI / 2.3, index * 0.05, side * (0.28 + index * 0.02)],
      ),
    );
  }
  return group;
}

function createCanopyGate(Three, clearance = 8) {
  const group = createRuinArch(Three, clearance);
  for (const side of [-1, 1]) {
    const tree = createTree(Three);
    tree.scale.setScalar(1.3);
    tree.position.set(side * 8.3, 0, 1);
    group.add(tree);
  }
  return group;
}

function createVineWall(Three, side = -1) {
  const mat = getMaterials(Three);
  const group = new Three.Group();
  for (let index = 0; index < 10; index += 1) {
    group.add(
      mesh(
        Three,
        new Three.CylinderGeometry(0.045, 0.065, 8.5 - (index % 3), 6),
        index % 2 ? mat.moss : mat.leaf,
        [side * (8.3 + (index % 3) * 0.55), 4.4, -7 + index * 1.55],
        [0.06 * (index % 2), 0, 0.04 * side],
      ),
    );
  }
  return group;
}

function createStartTotem(Three, side = -1) {
  const mat = getMaterials(Three);
  const group = new Three.Group();
  group.add(
    mesh(
      Three,
      new Three.BoxGeometry(1.4, 5.2, 1.1),
      mat.stone,
      [side * 8.5, 2.3, 0],
      [0.02, -0.08 * side, 0.04 * side],
    ),
  );
  for (let index = 0; index < 3; index += 1) {
    group.add(
      mesh(
        Three,
        new Three.TorusGeometry(0.36 + index * 0.08, 0.08, 7, 18),
        index === 1 ? mat.gold : mat.cyan,
        [side * 8.5, 3.8 - index * 0.8, -0.57],
      ),
    );
  }
  return group;
}

function createPortal(Three) {
  const mat = getMaterials(Three);
  const group = new Three.Group();
  group.add(mesh(Three, new Three.TorusGeometry(6.2, 0.34, 10, 52), mat.portal));
  group.add(
    mesh(
      Three,
      new Three.TorusGeometry(5.72, 0.12, 8, 52),
      mat.gold,
      [0, 0, -0.06],
    ),
  );
  group.add(
    mesh(
      Three,
      new Three.PlaneGeometry(10.8, 10.8),
      mat.portalCore,
      [0, 0, 0.18],
    ),
  );
  for (const side of [-1, 1]) {
    group.add(
      mesh(
        Three,
        new Three.BoxGeometry(0.72, 2.2, 0.82),
        mat.stone,
        [side * 6.1, -4.95, 0],
      ),
    );
  }
  group.position.y = 6;
  return group;
}

function addShardCluster(Three, group, material, side, count = 7, scale = 1) {
  for (let index = 0; index < count; index += 1) {
    const height = (1.8 + (index % 4) * 0.85) * scale;
    group.add(
      mesh(
        Three,
        new Three.ConeGeometry((0.45 + (index % 3) * 0.14) * scale, height, 5),
        material,
        [
          side * (8.2 + (index % 3) * 1.35),
          height * 0.5 - 0.4,
          -4 + index * 1.25,
        ],
        [0.08 * (index % 2), index * 0.42, side * 0.08],
      ),
    );
  }
}

function createIcefallSetpiece(Three, side) {
  const group = new Three.Group();
  const ice = themedMaterial(Three, "ice", 0x8de7ff, 0x1d9fd0, {
    emissiveIntensity: 0.8,
    roughness: 0.2,
    opacity: 0.78,
    side: Three.DoubleSide,
  });
  const water = themedMaterial(Three, "ice-water", 0x38bfff, 0x076fa8, {
    emissiveIntensity: 1,
    roughness: 0.1,
    opacity: 0.52,
    side: Three.DoubleSide,
  });
  group.add(
    mesh(
      Three,
      new Three.PlaneGeometry(7.5, 14, 5, 12),
      water,
      [side * 11.5, 5.5, -1],
      [0, side > 0 ? -0.12 : 0.12, 0],
    ),
  );
  addShardCluster(Three, group, ice, side, 8, 1.15);
  return group;
}

function createWhirlpoolSetpiece(Three, side) {
  const group = new Three.Group();
  const water = themedMaterial(Three, "whirlpool", 0x43d7ff, 0x006fb5, {
    emissiveIntensity: 1.35,
    opacity: 0.66,
    roughness: 0.12,
  });
  for (let ring = 0; ring < 5; ring += 1) {
    group.add(
      mesh(
        Three,
        new Three.TorusGeometry(1.8 + ring * 0.7, 0.11, 7, 36),
        water,
        [side * 11, -0.3 - ring * 0.08, 0],
        [Math.PI / 2, 0, ring * 0.25],
        [1, 0.72, 1],
      ),
    );
  }
  return group;
}

function createLavaSetpiece(Three, side, arch = false) {
  const group = new Three.Group();
  const rock = themedMaterial(Three, "lava-rock", 0x33151a, 0x4a0900, {
    emissiveIntensity: 0.38,
    roughness: 0.92,
  });
  const lava = themedMaterial(Three, "lava", 0xff5a16, 0xff2400, {
    emissiveIntensity: 2.2,
    roughness: 0.2,
  });
  if (arch) {
    for (const x of [-1, 1]) {
      group.add(
        mesh(
          Three,
          new Three.CylinderGeometry(0.9, 1.35, 8.5, 7),
          rock,
          [x * 7.2, 3.7, 0],
          [0.03, 0, x * 0.04],
        ),
      );
    }
    group.add(mesh(Three, new Three.BoxGeometry(13.2, 1.1, 1.7), rock, [0, 8.05, 0]));
    group.add(mesh(Three, new Three.BoxGeometry(8.8, 0.16, 1.8), lava, [0, 7.42, -0.15]));
  } else {
    for (let index = 0; index < 6; index += 1) {
      const height = 1.8 + (index % 3) * 0.7;
      group.add(
        mesh(
          Three,
          new Three.ConeGeometry(0.7 + index * 0.06, height, 7),
          index % 2 ? lava : rock,
          [side * (8.4 + (index % 2) * 1.8), height * 0.5 - 0.35, -4 + index * 1.5],
        ),
      );
    }
  }
  return group;
}

function createAsteroidSetpiece(Three, side, rig = false) {
  const group = new Three.Group();
  const rock = themedMaterial(Three, "asteroid", 0x706b65, 0x33281d, {
    emissiveIntensity: 0.28,
    roughness: 0.94,
  });
  const metal = themedMaterial(Three, "mining-metal", 0xc99a42, 0x5b3608, {
    emissiveIntensity: 0.52,
    metalness: 0.8,
    roughness: 0.3,
  });
  if (rig) {
    group.add(mesh(Three, new Three.BoxGeometry(5.8, 0.5, 3.4), metal, [side * 10.5, 1, 0]));
    group.add(mesh(Three, new Three.BoxGeometry(0.55, 7, 0.55), metal, [side * 10.5, 4, 0]));
    group.add(
      mesh(
        Three,
        new Three.BoxGeometry(5.5, 0.38, 0.45),
        metal,
        [side * 8.5, 7.2, 0],
        [0, 0, side * 0.3],
      ),
    );
  }
  for (let index = 0; index < (rig ? 4 : 9); index += 1) {
    const size = 0.9 + (index % 4) * 0.42;
    group.add(
      mesh(
        Three,
        new Three.DodecahedronGeometry(size, 0),
        rock,
        [
          side * (8 + (index % 3) * 2),
          1.2 + (index % 4) * 1.8,
          -6 + index * 1.45,
        ],
        [index * 0.42, index * 0.27, index * 0.16],
        [1.25, 0.82, 1],
      ),
    );
  }
  return group;
}

function createOceanSetpiece(Three, side, crystals = false) {
  const group = new Three.Group();
  const crystal = themedMaterial(Three, "ocean-crystal", 0x64f5ff, 0x087aaf, {
    emissiveIntensity: 1.3,
    roughness: 0.14,
    opacity: 0.84,
  });
  const water = themedMaterial(Three, "ocean-water", 0x167bc4, 0x064e83, {
    emissiveIntensity: 0.92,
    roughness: 0.08,
    opacity: 0.5,
    side: Three.DoubleSide,
  });
  if (!crystals) {
    group.add(
      mesh(
        Three,
        new Three.PlaneGeometry(8, 12, 4, 10),
        water,
        [side * 11.5, 4.8, 0],
      ),
    );
  }
  addShardCluster(Three, group, crystal, side, crystals ? 10 : 6, crystals ? 1.25 : 0.8);
  return group;
}

function createSatelliteSetpiece(Three, side, gate = false) {
  const group = new Three.Group();
  const metal = themedMaterial(Three, "sat-metal", 0x9fb6d9, 0x203d72, {
    emissiveIntensity: 0.45,
    metalness: 0.9,
    roughness: 0.22,
  });
  const panel = themedMaterial(Three, "sat-panel", 0x244f9b, 0x0c45a6, {
    emissiveIntensity: 0.9,
    metalness: 0.65,
    roughness: 0.18,
  });
  const scan = themedMaterial(Three, "sat-scan", 0xff4c76, 0xff164e, {
    emissiveIntensity: 2,
    roughness: 0.12,
    opacity: 0.72,
  });
  if (gate) {
    for (const x of [-1, 1]) {
      group.add(mesh(Three, new Three.BoxGeometry(0.7, 8, 0.8), metal, [x * 7.2, 4, 0]));
    }
    group.add(mesh(Three, new Three.BoxGeometry(14.5, 0.7, 0.85), metal, [0, 8, 0]));
    group.add(mesh(Three, new Three.BoxGeometry(9.5, 0.12, 0.18), scan, [0, 6.5, 0]));
  } else {
    group.add(mesh(Three, new Three.CylinderGeometry(0.6, 0.6, 6, 12), metal, [side * 10, 3, 0]));
    for (const z of [-2.2, 2.2]) {
      group.add(mesh(Three, new Three.BoxGeometry(5.8, 0.16, 3.2), panel, [side * 10, 4.8, z]));
    }
  }
  return group;
}

function createMistSetpiece(Three, side, spires = false) {
  const group = new Three.Group();
  const stone = themedMaterial(Three, "mist-stone", 0x5f6989, 0x202b55, {
    emissiveIntensity: 0.48,
    roughness: 0.72,
    opacity: spires ? 0.58 : 1,
  });
  for (let index = 0; index < (spires ? 8 : 6); index += 1) {
    const height = 3.5 + (index % 4) * 1.35;
    group.add(
      mesh(
        Three,
        spires
          ? new Three.ConeGeometry(0.55 + (index % 2) * 0.2, height, 5)
          : new Three.CylinderGeometry(0.65, 0.9, height, 6),
        stone,
        [side * (8.4 + (index % 3) * 1.45), height * 0.5 - 0.5, -5 + index * 1.5],
        [0.03 * index, index * 0.22, side * 0.04],
      ),
    );
  }
  return group;
}

function createNebulaSetpiece(Three, side, storm = false) {
  const group = new Three.Group();
  const plasma = themedMaterial(Three, "plasma", 0xf25cff, 0x8c1cff, {
    emissiveIntensity: 2,
    roughness: 0.1,
    opacity: 0.78,
  });
  const dark = themedMaterial(Three, "plasma-dark", 0x35205f, 0x301070, {
    emissiveIntensity: 0.75,
    metalness: 0.55,
    roughness: 0.3,
  });
  for (let index = 0; index < 5; index += 1) {
    group.add(
      mesh(
        Three,
        new Three.TorusGeometry(1.4 + index * 0.6, 0.12, 7, 28),
        plasma,
        [side * 10.5, 3.2 + index * 0.35, 0],
        [0.4 + index * 0.14, index * 0.3, index * 0.22],
      ),
    );
  }
  if (storm) {
    for (const x of [-1, 1]) {
      group.add(mesh(Three, new Three.CylinderGeometry(0.35, 0.6, 6, 7), dark, [x * 7.4, 3, 0]));
    }
    group.add(mesh(Three, new Three.BoxGeometry(13.8, 0.12, 0.12), plasma, [0, 7.2, 0]));
  }
  return group;
}

function createGauntletSetpiece(Three, side, gate = false, intensity = 1) {
  const group = gate
    ? createLavaSetpiece(Three, side, true)
    : createNebulaSetpiece(Three, side, true);
  const core = themedMaterial(Three, "gauntlet-core", 0xffbd38, 0xff2d00, {
    emissiveIntensity: 2.5,
    metalness: 0.35,
    roughness: 0.16,
  });
  const rings = Math.min(6, 2 + Math.floor(intensity / 2));
  for (let index = 0; index < rings; index += 1) {
    group.add(
      mesh(
        Three,
        new Three.TorusGeometry(1.2 + index * 0.55, 0.1, 6, 28),
        core,
        [side * 10, 4.2, 0],
        [index * 0.18, index * 0.34, index * 0.12],
      ),
    );
  }
  return group;
}

function createWorldSetpiece(Three, definition) {
  const side = definition.side || 1;
  switch (`${definition.world}:${definition.variant}`) {
    case "waterfall:icefall":
      return createIcefallSetpiece(Three, side);
    case "waterfall:whirlpool":
      return createWhirlpoolSetpiece(Three, side);
    case "redheat:lava-vent":
      return createLavaSetpiece(Three, side, false);
    case "redheat:magma-arch":
      return createLavaSetpiece(Three, side, true);
    case "asteroid:asteroid-field":
      return createAsteroidSetpiece(Three, side, false);
    case "asteroid:mining-rig":
      return createAsteroidSetpiece(Three, side, true);
    case "blueplanet:reef-fall":
      return createOceanSetpiece(Three, side, false);
    case "blueplanet:crystal-reef":
      return createOceanSetpiece(Three, side, true);
    case "satellite:docking-array":
      return createSatelliteSetpiece(Three, side, false);
    case "satellite:scan-gate":
      return createSatelliteSetpiece(Three, side, true);
    case "misty:mist-pillars":
      return createMistSetpiece(Three, side, false);
    case "misty:ghost-spires":
      return createMistSetpiece(Three, side, true);
    case "nebula:plasma-crown":
      return createNebulaSetpiece(Three, side, false);
    case "nebula:ion-storm":
      return createNebulaSetpiece(Three, side, true);
    case "boss:gauntlet-gate":
      return createGauntletSetpiece(
        Three,
        side,
        true,
        definition.intensity,
      );
    case "boss:gauntlet-forge":
      return createGauntletSetpiece(
        Three,
        side,
        false,
        definition.intensity,
      );
    default:
      return null;
  }
}

function createLandmark(Three, definition) {
  switch (definition.type) {
    case "giant-tree":
      return createTree(Three, { giant: true });
    case "waterfall":
      return createWaterfall(Three, definition.side);
    case "ruin-arch":
      return createRuinArch(Three, definition.clearance);
    case "temple-court":
      return createTempleCourt(Three);
    case "root-bridge":
      return createRootBridge(Three, definition.side);
    case "canopy-gate":
      return createCanopyGate(Three, definition.clearance);
    case "vine-wall":
      return createVineWall(Three, definition.side);
    case "start-totem":
      return createStartTotem(Three, definition.side);
    case "portal":
      return createPortal(Three);
    case "world-setpiece":
      return createWorldSetpiece(Three, definition);
    default:
      return null;
  }
}

export function decorateRow(rowIndex, cells, rowGroup) {
  const Three = window.__orbitrushThreeContext;
  if (!Three) return;
  const z = -(rowIndex + 0.5) * ROW_LENGTH;

  for (const lane of cells._trees || []) {
    const tree = createTree(Three);
    tree.position.set((lane - 2) * LANE_WIDTH, 1.05, z);
    tree.scale.setScalar(0.86);
    rowGroup.add(tree);
  }

  for (const definition of cells._landmarks || []) {
    const landmark = createLandmark(Three, definition);
    if (!landmark) continue;
    if (definition.type === "giant-tree") {
      landmark.position.set((definition.side || 1) * 12.5, 0, z - 5);
    } else if (definition.type === "portal") {
      landmark.position.z = z - 0.25;
    } else {
      landmark.position.z += z;
    }
    if (definition.scale) landmark.scale.multiplyScalar(definition.scale);
    rowGroup.add(landmark);
  }
}
